import React, { useState, useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { NullRowData } from "@features/onboarding/data-migration/types";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import {
  useCategoryOptions,
  usePlanOptions,
  useConsumerStatusOptions,
  useMeterTypeOptions,
  useMeterManufacturerOptions,
  useMeterModelOptions,
  useUtilityServicesOptions,
  useGroupedRelationData,
  useAssetClassOptions,
  useAssetTypeOptions,
  useBackupStatusOptions,
  useDepreciationMethodOptions,
  useDepreciationScheduleOptions,
  useFacilityStatusOptions,
  useFacilityTypeOptions,
  useManufacturerAXOptions,
  useNetworkTypeOptions,
  useOperationalHoursOptions,
  useSystemTypeOptions,
  useTerritoryZoneOptions,
  useUtilityServicesAXOptions,
  useNullRowsData,
  useManagers,
  usePremiseOptions,
  useOperationalStatusOptions,
  useSystemStatusOptions,
} from "../../../hooks";
import {
  AssetClassOption,
  ChoiceOption,
  GroupedValue,
  ManufacturerOption,
  TerritoryOption,
  UtilityServiceAXOption,
} from "@features/onboarding/data-migration/types";
import { DataRelationFilter } from "../filter/DataRelationshipFilter";
import { useLocation } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { PAGE_SIZE } from "@shared/utils/constants";
import { toSnakeCase } from "@shared/utils/caseConverter";

interface NullRowsTableProps {
  sessionId: string;
  dataType: string;
  tabName: string;
  targetField: string;
  onPageChange: (page: number) => void;
  onAssign: (
    rowIndices: number[],
    targetId: number | string,
    targetName: string
  ) => void;
  isAssigning: boolean;
  assignmentState: Record<number, Assignment>;
  onAssignmentChange: (assignments: Record<number, Assignment>) => void;
  allTabsAssignmentState: Record<string, Record<number, Assignment>>;
  relationTabs: Array<{
    name: string;
    targetField: string;
    displayName: string;
  }>;
}

// Local assignment state structure
interface Assignment {
  targetId: number | string;
  targetName: string;
  timestamp: number;
}

export const NullRowsTable: React.FC<NullRowsTableProps> = ({
  sessionId,
  dataType,
  tabName,
  targetField,
  onPageChange,
  onAssign,
  isAssigning,
  assignmentState,
  onAssignmentChange,
  allTabsAssignmentState,
  relationTabs,
}) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const urlTab = searchParams.get("tab") || tabName;
  const searchTerm = searchParams.get("search") || "";

  // Format filters for the hook
  const currentFilters = useMemo(() => {
    const filters: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith("filter_")) {
        const filterKey = key.replace("filter_", "");

        // ✅ Handle comma-separated values (convert back to array for multi-select)
        if (value.includes(",")) {
          filters[filterKey] = value.split(",");
        } else {
          filters[filterKey] = value;
        }
      }
    });
    return {
      search: searchTerm,
      ...filters,
    };
  }, [searchParams, searchTerm]);

  // ✅ Fetch null rows data with correct parameter order
  const { data: nullRowsData, isLoading } = useNullRowsData(
    sessionId,
    tabName,
    currentPage,
    PAGE_SIZE,
    currentFilters,
    true
  );

  // ✅ Fetch grouped data for searchableColumns
  const { data: groupedData } = useGroupedRelationData(
    sessionId,
    tabName,
    {},
    true
  );

  const rows = nullRowsData?.rows || [];
  const totalItems = nullRowsData?.totalRows || 0;
  const itemsPerPage = nullRowsData?.pageSize || PAGE_SIZE;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      startItem,
      endItem,
    }),
    [currentPage, totalPages, totalItems, itemsPerPage, startItem, endItem]
  );

  const { data: planData, isLoading: isPlanLoading } = usePlanOptions(
    urlTab === "plan"
  );
  const { data: categoryData, isLoading: isCategoryLoading } =
    useCategoryOptions(
      urlTab,
      urlTab === "category" || urlTab === "sub_category"
    );
  const { data: statusData, isLoading: isStatusLoading } =
    dataType == "Systems"
      ? useSystemStatusOptions(urlTab === "status")
      : useConsumerStatusOptions(urlTab === "status");

  const { data: meterTypeData, isLoading: isMeterTypeLoading } =
    useMeterTypeOptions(urlTab === "meter_type");
  const { data: manufacturerData, isLoading: isManufacturerLoading } =
    useMeterManufacturerOptions(urlTab === "meter_manufacturer");
  const { data: meterModelData, isLoading: isMeterModelLoading } =
    useMeterModelOptions(urlTab === "meter_model");
  const { data: utilityServicesData, isLoading: isUtilityServicesLoading } =
    useUtilityServicesOptions(urlTab === "utility_service");
  // ✅ ADD: Asset Management hooks
  const { data: assetClassData, isLoading: isAssetClassLoading } =
    useAssetClassOptions(urlTab === "asset_class");
  const { data: assetTypeData, isLoading: isAssetTypeLoading } =
    useAssetTypeOptions(urlTab === "asset_type");
  const { data: manufacturerAXData, isLoading: isManufacturerAXLoading } =
    useManufacturerAXOptions(urlTab === "meter_manufacturer", 1, 1000);
  const {
    data: depreciationMethodData,
    isLoading: isDepreciationMethodLoading,
  } = useDepreciationMethodOptions(urlTab === "depreciation_method");
  const { data: networkTypeData, isLoading: isNetworkTypeLoading } =
    useNetworkTypeOptions(urlTab === "network_type");
  const { data: facilityStatusData, isLoading: isFacilityStatusLoading } =
    useFacilityStatusOptions(
      urlTab === "operating_status" || urlTab === "facility_status"
    );
  const { data: backupStatusData, isLoading: isBackupStatusLoading } =
    useBackupStatusOptions(urlTab === "backup_status");
  const { data: systemTypeData, isLoading: isSystemTypeLoading } =
    useSystemTypeOptions(urlTab === "system_type");
  const { data: operationalHoursData, isLoading: isOperationalHoursLoading } =
    useOperationalHoursOptions(urlTab === "operational_hours");

  const { data: operationalStatusData, isLoading: isOperationalStatusLoading } =
    useOperationalStatusOptions(urlTab === "operational_status");
  const { data: facilityTypeData, isLoading: isFacilityTypeLoading } =
    useFacilityTypeOptions(urlTab === "facility_type");
  const { data: utilityServicesAXData, isLoading: isUtilityServicesAXLoading } =
    useUtilityServicesAXOptions(urlTab === "utility_service_ax");
  const { data: territoryZoneData, isLoading: isTerritoryZoneLoading } =
    useTerritoryZoneOptions(urlTab === "service_zone_division");
  const {
    data: depreciationScheduleData,
    isLoading: isDepreciationScheduleLoading,
  } = useDepreciationScheduleOptions(urlTab === "depreciation_schedule");
  const { data: facilityManager, isLoading: isFacilityManagerLoading } =
    useManagers(urlTab === "network_operator");
  const { data: premiseData, isLoading: isPremiseLoading } = usePremiseOptions(
    urlTab === "billing_premise" || urlTab === "service_premise"
  );

  // ✅ Then select the appropriate data based on urlTab
  const { optionsData, isOptionsLoading } = useMemo(() => {
    switch (urlTab) {
      case "plan":
        return { optionsData: planData || [], isOptionsLoading: isPlanLoading };
      case "category":
      case "sub_category":
        return {
          optionsData: categoryData || [],
          isOptionsLoading: isCategoryLoading,
        };
      case "status":
        return {
          optionsData: statusData || [],
          isOptionsLoading: isStatusLoading,
        };
      case "meter_type":
        return {
          optionsData: meterTypeData || [],
          isOptionsLoading: isMeterTypeLoading,
        };
      case "meter_manufacturer":
        return {
          optionsData: manufacturerData || [],
          isOptionsLoading: isManufacturerLoading,
        };
      case "meter_model":
        return {
          optionsData: meterModelData || [],
          isOptionsLoading: isMeterModelLoading,
        };
      case "utility_service":
        return {
          optionsData: utilityServicesData || [],
          isOptionsLoading: isUtilityServicesLoading,
        };

      // ✅ Asset Management cases
      case "asset_class":
        return {
          optionsData: assetClassData || [],
          isOptionsLoading: isAssetClassLoading,
        };
      case "asset_type":
        return {
          optionsData: assetTypeData || [],
          isOptionsLoading: isAssetTypeLoading,
        };
      case "depreciation_method":
        return {
          optionsData: depreciationMethodData || [],
          isOptionsLoading: isDepreciationMethodLoading,
        };
      case "network_type":
        return {
          optionsData: networkTypeData || [],
          isOptionsLoading: isNetworkTypeLoading,
        };
      case "operating_status":
      case "facility_status":
        return {
          optionsData: facilityStatusData || [],
          isOptionsLoading: isFacilityStatusLoading,
        };
      case "backup_status":
        return {
          optionsData: backupStatusData || [],
          isOptionsLoading: isBackupStatusLoading,
        };
      case "system_type":
        return {
          optionsData: systemTypeData || [],
          isOptionsLoading: isSystemTypeLoading,
        };
      case "operational_hours":
        return {
          optionsData: operationalHoursData || [],
          isOptionsLoading: isOperationalHoursLoading,
        };
      case "operational_status":
        return {
          optionsData: operationalStatusData || [],
          isOptionsLoading: isOperationalStatusLoading,
        };
      case "facility_type":
        return {
          optionsData: facilityTypeData || [],
          isOptionsLoading: isFacilityTypeLoading,
        };
      case "utility_service_ax":
        return {
          optionsData: utilityServicesAXData || [],
          isOptionsLoading: isUtilityServicesAXLoading,
        };
      case "service_zone_division":
        return {
          optionsData: territoryZoneData || [],
          isOptionsLoading: isTerritoryZoneLoading,
        };
      case "depreciation_schedule":
        return {
          optionsData: depreciationScheduleData || [],
          isOptionsLoading: isDepreciationScheduleLoading,
        };
      case "network_operator":
        return {
          optionsData: facilityManager || [],
          isOptionsLoading: isFacilityManagerLoading,
        };
      case "billing_premise":
      case "service_premise":
        return {
          optionsData: premiseData?.result || [],
          isOptionsLoading: isPremiseLoading,
        };
      case "data_type":
        return {
          optionsData: [
            { key: "text", value: "Text" },
            { key: "number", value: "Number" },
            { key: "dropdown", value: "Dropdown" },
            { key: "date", value: "Date" },
          ],
          isOptionsLoading: false,
        };

      default:
        return { optionsData: [], isOptionsLoading: false };
    }
  }, [
    urlTab,
    planData,
    categoryData,
    statusData,
    meterTypeData,
    manufacturerData,
    meterModelData,
    utilityServicesData,
    assetClassData,
    assetTypeData,
    manufacturerAXData,
    depreciationMethodData,
    networkTypeData,
    facilityStatusData,
    backupStatusData,
    systemTypeData,
    operationalHoursData,
    facilityTypeData,
    utilityServicesAXData,
    territoryZoneData,
    depreciationScheduleData,
    facilityManager,
    premiseData,
    isPlanLoading,
    isCategoryLoading,
    isStatusLoading,
    isMeterTypeLoading,
    isManufacturerLoading,
    isMeterModelLoading,
    isUtilityServicesLoading,
    isAssetClassLoading,
    isAssetTypeLoading,
    isManufacturerAXLoading,
    isDepreciationMethodLoading,
    isNetworkTypeLoading,
    isFacilityStatusLoading,
    isBackupStatusLoading,
    isSystemTypeLoading,
    isOperationalHoursLoading,
    isOperationalStatusLoading,
    isFacilityTypeLoading,
    isUtilityServicesAXLoading,
    isTerritoryZoneLoading,
    isDepreciationScheduleLoading,
    isFacilityManagerLoading,
    isFacilityManagerLoading,
    isPremiseLoading,
  ]);

  // Map dropdown options based on tab type
  const dropdownOptions = useMemo<{ value: string; label: string }[]>(() => {
    if (!optionsData) return [];

    switch (urlTab) {
      case "plan":
        return optionsData.map((p: any) => ({
          value: String(p.id),
          label: p.planName,
        }));
      case "category":
      case "sub_category":
        return optionsData.map((c: any) => ({
          value: c.name,
          label: c.name,
        }));
      case "status":
        return optionsData.map((s: any) => ({
          value: String(s.key),
          label: s.value,
        }));
      case "meter_type":
        return optionsData.map((t: any) => ({
          value: String(t.key),
          label: t.value,
        }));
      case "meter_manufacturer":
        return optionsData.map((m: any) => ({
          value: String(m.id),
          label: m.name,
        }));
      case "meter_model":
        return optionsData.map((mm: any) => ({
          value: String(mm.id),
          label: mm.meterModel,
        }));
      case "utility_service":
        return optionsData
          .filter((s: any) => s.isActive)
          .map((s: any) => ({
            value: String(s.id),
            label: s.name,
          }));
      case "asset_class":
      case "asset_type":
      case "system_type":
      case "facility_type":
        return optionsData.map((item: AssetClassOption) => ({
          value: item.code,
          label: item.name,
        }));

      case "depreciation_method":
      case "network_type":
      case "operating_status":
      case "facility_status":
      case "backup_status":
      case "operational_hours":
      case "operational_status":
      case "depreciation_schedule":
        return optionsData.map((choice: ChoiceOption) => ({
          value: String(choice.key),
          label: choice.value,
        }));

      case "utility_service_ax":
        return optionsData
          .filter((s: UtilityServiceAXOption) => s.isActive)
          .map((s: UtilityServiceAXOption) => ({
            value: String(s.id),
            label: s.name,
          }));

      case "service_zone_division":
        return optionsData?.map((zone: TerritoryOption) => ({
          value: zone.code,
          label: zone.name,
        }));
      case "network_operator":
        return (
          optionsData?.result?.map((fm: any) => ({
            value: String(fm.user.id),
            label: fm.user.name,
          })) || []
        );

      case "billing_premise":
      case "service_premise":
        return optionsData.map((premise: any) => ({
          value: premise.code,
          label: premise.name,
        }));
      case "data_type":
        return optionsData.map((item: any) => ({
          value: item.key,
          label: item.value,
        }));

      default:
        return [];
    }
  }, [urlTab, optionsData]);

  // Enrich rows with assignments from local state
  const enrichedRows = useMemo(() => {
    if (!rows || rows.length === 0) {
      return [];
    }

    return rows.map((row) => {
      // Start with original data
      const updatedData = { ...row.data };

      // ✅ Apply assignments from ALL tabs for this rowIndex
      Object.entries(allTabsAssignmentState).forEach(
        ([otherTabName, assignments]) => {
          const assignment = assignments[row.rowIndex];
          if (assignment) {
            // Find the target field for this tab
            const otherTab = relationTabs.find((t) => t.name === otherTabName);
            if (otherTab) {
              // ✅ FIX: Find the actual field key in the data that matches this target field
              const actualFieldKey = Object.keys(updatedData).find(
                (key) => toSnakeCase(key) === otherTab.targetField
              );

              // Only apply if we found a matching field
              if (actualFieldKey) {
                updatedData[actualFieldKey] = assignment.targetName;
              }
            }
          }
        }
      );

      // Check if current tab has assignment
      const currentTabAssignment = assignmentState[row.rowIndex];

      return {
        ...row,
        data: updatedData,
        hasAssignment: !!currentTabAssignment,
      };
    });
  }, [
    rows,
    assignmentState,
    allTabsAssignmentState,
    relationTabs,
    targetField,
  ]);

  // Create table columns
  const tableColumns: TableColumn<any>[] = useMemo(() => {
    if (enrichedRows.length === 0) {
      return [];
    }

    const allKeys = Object.keys(enrichedRows[0].data);

    // ✅ Convert all keys to snake_case for comparison
    const normalizedTargetField = targetField;

    // ✅ Find all variations of the target field (with/without spaces, different cases)
    const targetFieldVariations = allKeys.filter(
      (key) => toSnakeCase(key) === normalizedTargetField
    );

    // ✅ Keep only the FIRST variation of target field, remove all others
    const targetFieldKey = targetFieldVariations[0];
    const otherFields = allKeys.filter(
      (key) => toSnakeCase(key) !== normalizedTargetField
    );

    // ✅ Reorder: target field first, then others (without any duplication)
    const orderedKeys = targetFieldKey
      ? [targetFieldKey, ...otherFields]
      : allKeys;

    return orderedKeys.map((fieldKey) => {
      const isTargetField = toSnakeCase(fieldKey) === normalizedTargetField;

      return {
        key: fieldKey,
        header: fieldKey
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        type: "custom",
        render: (value: any, row: any) => {
          const rowData = enrichedRows.find((r) => r.rowIndex == row.id);
          const hasAssignment = rowData?.hasAssignment;
          const assignment = assignmentState[row.id];

          // Only show enhanced badges for the target field column
          if (isTargetField) {
            if (hasAssignment && assignment) {
              return (
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="border-green-500 text-green-700 bg-green-50 font-medium"
                  >
                    {assignment.targetName} ✓
                  </Badge>
                </div>
              );
            } else if (!value && !assignment) {
              return (
                <Badge
                  variant="destructive"
                  className="bg-red-50 text-red-700 border-red-200"
                >
                  Empty
                </Badge>
              );
            }
          }

          const fieldSnakeCase = toSnakeCase(fieldKey);
          let crossTabAssignment = null;

          // Look through all tabs to see if this field was assigned elsewhere
          Object.entries(allTabsAssignmentState).forEach(
            ([otherTabName, assignments]) => {
              if (otherTabName !== tabName) {
                // Not current tab
                const otherTab = relationTabs.find(
                  (t) => t.name === otherTabName
                );
                if (
                  otherTab &&
                  toSnakeCase(otherTab.targetField) === fieldSnakeCase
                ) {
                  const assignment = assignments[row.id];
                  if (assignment) {
                    crossTabAssignment = assignment.targetName;
                  }
                }
              }
            }
          );

          // ✅ If cross-tab assignment exists, show with yellow badge
          if (crossTabAssignment) {
            return (
              <Badge
                variant="outline"
                className="border-yellow-500 text-yellow-700 bg-yellow-50 font-medium"
              >
                {crossTabAssignment}
              </Badge>
            );
          }

          return <span>{value}</span>;
        },
      };
    });
  }, [
    enrichedRows,
    targetField,
    assignmentState,
    allTabsAssignmentState,
    relationTabs,
  ]);

  const tableData = useMemo(() => {
    return enrichedRows.map((row) => ({
      id: row.rowIndex,
      ...row.data,
    }));
  }, [enrichedRows]);

  // Handle assignment
  const handleAssign = () => {
    if (!selectedValue || selectedRows.length === 0) return;

    const option = dropdownOptions.find((opt) => opt.value === selectedValue);
    if (!option) return;

    let targetId: number | string;

    if (urlTab === "meter_type" || urlTab === "status") {
      targetId = parseInt(selectedValue);
    } else if (
      urlTab === "utility_service" ||
      urlTab === "manufacturer" ||
      urlTab === "meter_model" ||
      urlTab === "plan"
    ) {
      targetId = parseInt(selectedValue);
    } else {
      targetId = selectedValue;
    }

    const rowIndices = selectedRows.map((id) => parseInt(id));

    // ✅ UPDATE: Use the callback from parent
    const newState = { ...assignmentState };
    rowIndices.forEach((rowIndex) => {
      newState[rowIndex] = {
        targetId: targetId as any,
        targetName: option.label,
        timestamp: Date.now(),
      };
    });
    onAssignmentChange(newState);

    onAssign(rowIndices, targetId as any, option.label);

    setSelectedRows([]);
    setSelectedValue("");
  };

  // Count assigned rows
  const assignedCount = Object.keys(assignmentState).length;

  const handleSearch = (searchValue: string, paramName: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (searchValue.trim()) {
      newParams.set(paramName, searchValue.trim());
      newParams.set("page", "1");
    } else {
      newParams.delete(paramName);
    }
    setSearchParams(newParams);
  };

  // ✅ ADD: Handle pagination internally
  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
    setCurrentPage(page);
  };

  // ✅ ADD: Handle filter apply
  const handleApplyFilters = (newFilters: any) => {
    const newParams = new URLSearchParams(searchParams);

    // Clear existing filter params
    Array.from(newParams.keys()).forEach((key) => {
      if (key.startsWith("filter_")) {
        newParams.delete(key);
      }
    });

    // Add new filters
    Object.entries(newFilters).forEach(([key, value]) => {
      // ✅ Handle arrays (for multi-select like zipcode)
      if (Array.isArray(value)) {
        if (value.length > 0) {
          newParams.set(`filter_${key}`, value.join(",")); // Join array as comma-separated
        }
      }
      // ✅ Handle regular values
      else if (value && value !== "all" && value !== "") {
        newParams.set(`filter_${key}`, String(value));
      }
    });

    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  // ✅ ADD: Handle filter reset
  const handleResetFilters = () => {
    const newParams = new URLSearchParams();
    const currentTabParam = searchParams.get("tab");

    if (currentTabParam) {
      newParams.set("tab", currentTabParam);
    }

    newParams.set("page", "1");
    setSearchParams(newParams);
  };
  return (
    <>
      <div className="space-y-4 p-4 bg-white">
        {assignedCount > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-green-700">
                <span className="font-medium">{assignedCount}</span> row
                {assignedCount !== 1 ? "s" : ""} assigned and saved
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAssignmentChange({})} // ✅ UPDATE: Use callback
              >
                Clear View
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
          <div className="text-sm text-gray-600">
            {selectedRows.length > 0 ? (
              <>
                <span className="font-medium">{selectedRows.length}</span> row
                {selectedRows.length !== 1 ? "s" : ""} selected
              </>
            ) : (
              <span>Select rows to assign values</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Select
              value={selectedValue}
              onValueChange={setSelectedValue}
              disabled={selectedRows.length === 0 || isOptionsLoading}
            >
              <SelectTrigger className="w-[250px]">
                <SelectValue
                  placeholder={
                    isOptionsLoading ? "Loading options..." : "Select value..."
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {dropdownOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={handleAssign}
              disabled={
                !selectedValue || selectedRows.length === 0 || isAssigning
              }
            >
              {isAssigning
                ? "Assigning..."
                : `Assign to Selected (${selectedRows.length})`}
            </Button>
          </div>
        </div>
      </div>
      <GenericTable
        data={tableData}
        columns={tableColumns}
        rowKey="id"
        loading={isLoading}
        searchPlaceholder="Search in null rows..."
        onSearch={handleSearch}
        pagination={pagination}
        onPageChange={handlePageChange}
        emptyMessage="No null value rows found"
        selectable={true}
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        filterPanel={
          <DataRelationFilter
            dataType={dataType}
            currentFilters={currentFilters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
      />
    </>
  );
};
